import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Github, Loader2 } from "lucide-react";

import { useAuthStatus } from "@/lib/auth";

type DeviceStart = {
  user_code: string;
  verification_uri: string;
  expires_at: string;
  interval_s: number;
};

type DeviceStatus = {
  status: "idle" | "pending" | "complete" | "expired" | "denied" | "error";
  user_code?: string;
  verification_uri?: string;
  expires_at?: string;
  error?: string;
};

const STATUS_POLL_MS = 3_000;

// LoginScreen is shown when /api/auth/status reports unauthenticated.
// It walks the user through GitHub's device authorization grant:
//
//   1. Click "Login with GitHub" → POST /api/auth/device/start
//   2. Show the user_code (with copy button) and a button that opens
//      the verification_uri in a new tab.
//   3. Poll /api/auth/device/status until terminal.
//   4. On "complete", the parent's useAuthStatus picks up the new
//      token on its next tick and unmounts the login screen.
export default function LoginScreen() {
  const [start, setStart] = useState<DeviceStart | null>(null);
  const [status, setStatus] = useState<DeviceStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beginFlow = async () => {
    setBusy(true);
    setError(null);
    setStart(null);
    setStatus(null);
    try {
      const res = await fetch("/api/auth/device/start", { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as DeviceStart;
      setStart(data);
      setStatus({ status: "pending", user_code: data.user_code, verification_uri: data.verification_uri });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Poll the server-side flow state while a flow is in progress. Stop
  // as soon as we hit a terminal status; the parent's auth status
  // poller (in useAuthStatus) handles the transition out of this
  // screen on completion.
  useEffect(() => {
    if (!start || (status?.status && status.status !== "pending")) return;
    const id = window.setInterval(async () => {
      try {
        const res = await fetch("/api/auth/device/status");
        if (!res.ok) return;
        const data = (await res.json()) as DeviceStatus;
        setStatus(data);
      } catch {
        /* swallow transient errors; next tick will retry */
      }
    }, STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [start, status?.status]);

  const copyCode = async () => {
    if (!start) return;
    try {
      await navigator.clipboard.writeText(start.user_code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <img
            src="/icon.png"
            alt=""
            aria-hidden="true"
            className="h-8 w-8 rounded-md object-contain"
          />
          <h1 className="text-lg font-semibold">Sign in to bee</h1>
        </div>

        <p className="mb-6 text-sm text-muted-foreground">
          bee needs a GitHub token to read PRs and run reviews. Click below to start a
          device authorization flow — the same one <code className="font-mono">gh auth login</code> uses. The
          resulting token is stored via <code className="font-mono">gh</code> and shared with the Copilot CLI in
          this container.
        </p>

        {!start ? (
          <button
            type="button"
            onClick={beginFlow}
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-3 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Github className="h-4 w-4" />}
            Login with GitHub
          </button>
        ) : (
          <DeviceFlowPanel start={start} status={status} copied={copied} onCopy={copyCode} onRestart={beginFlow} />
        )}

        {error ? (
          <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DeviceFlowPanel({
  start,
  status,
  copied,
  onCopy,
  onRestart,
}: {
  start: DeviceStart;
  status: DeviceStatus | null;
  copied: boolean;
  onCopy: () => void;
  onRestart: () => void;
}) {
  const stage = status?.status ?? "pending";

  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Your one-time code</div>
        <div className="mt-1 flex items-center gap-2">
          <code className="rounded bg-muted px-3 py-2 font-mono text-lg tracking-widest">{start.user_code}</code>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <a
        href={start.verification_uri}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium hover:bg-muted"
      >
        Open GitHub
        <ExternalLink className="h-3 w-3" />
      </a>

      <FlowStatusLine stage={stage} error={status?.error} />

      {stage === "expired" || stage === "denied" || stage === "error" ? (
        <button
          type="button"
          onClick={onRestart}
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          Start over
        </button>
      ) : null}
    </div>
  );
}

function FlowStatusLine({ stage, error }: { stage: string; error?: string }) {
  switch (stage) {
    case "pending":
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Waiting for you to approve in GitHub…
        </div>
      );
    case "complete":
      return (
        <div className="flex items-center gap-2 text-xs text-green-700">
          <Check className="h-3 w-3" />
          Authorized — bee is loading. Restart the bee container if the worker doesn't pick up the new token automatically.
        </div>
      );
    case "expired":
      return <div className="text-xs text-yellow-700">Code expired. Start over.</div>;
    case "denied":
      return <div className="text-xs text-red-700">Authorization denied.</div>;
    case "error":
      return <div className="text-xs text-red-700">{error || "Unknown error"}</div>;
    default:
      return null;
  }
}

// AuthGate renders LoginScreen until the user is authenticated, then
// renders children. Place it at the App root so every page is
// auto-gated without needing per-page wiring.
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { status, loading } = useAuthStatus();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Checking authentication…
      </div>
    );
  }

  if (!status?.authenticated) {
    return <LoginScreen />;
  }

  return <>{children}</>;
}
